import ShoppingBag from "@components/icons/ShoppingBag"
import Spinner from "@components/icons/Spinner"
import { _getAccountBalance } from "@lib/getAccountBalance"
import Check from "@components/icons/Check"
import { Button, ButtonProps } from "../ui/base/button"
import { useCheckout } from "@/src/lib/cart/handleCheckout"
import SliceLogo from "../icons/SliceLogo"
import { useSliceContext } from "../SliceProvider"
import { useAccount } from "wagmi"
import { cn } from "@src/lib/utils"

export type ErrorState = {
  message: string
  type?: "" | "error"
}

const CheckoutButton = ({
  buttonProps,
  label,
  onSuccess,
  onError
}: {
  buttonProps: ButtonProps
  label?: string
  onSuccess?: () => void | Promise<void>
  onError?: () => void | Promise<void>
}) => {
  const { checkout, errorState, isLoading, loadingState, isSuccess } =
    useCheckout({
      onSuccess,
      onError
    })

  const {
    isUpdatingCart,
    cart,
    checkoutCompleted,
    isConnected,
    isReady,
    theme
  } = useSliceContext()
  const { accentBackgroundColor } = theme

  const { address: account, connector } = useAccount()

  const isNotPurchaseable = cart?.some(
    (item) =>
      item.isAllowed === false ||
      (item.maxUnitsPerBuyer > 0 && item.quantity > item.maxUnitsPerBuyer)
  )

  const isDisabled =
    isConnected &&
    account &&
    (!connector ||
      isNotPurchaseable ||
      !isReady ||
      isUpdatingCart ||
      isLoading ||
      !cart.length ||
      checkoutCompleted ||
      (!!errorState?.message && errorState?.type != "error"))

  return (
    <div>
      <Button
        type="button"
        size="lg"
        onClick={checkout}
        disabled={isDisabled}
        {...buttonProps}
        className={cn(
          `sl-flex sl-items-center sl-justify-center sl-gap-2 sl-w-full  ${
            isSuccess
              ? "sl-bg-green-500 !sl-opacity-100"
              : errorState?.type === "error"
              ? "!sl-bg-red-500 hover:!sl-bg-red-700 !sl-text-white"
              : "sl-bg-black"
          }`,
          accentBackgroundColor,
          buttonProps?.className
        )}
      >
        {isLoading ? (
          <>
            {loadingState && <p>{loadingState}</p>}
            <Spinner />
          </>
        ) : errorState?.message ? (
          <p>{errorState.message}</p>
        ) : isSuccess ? (
          <>
            <p>Checkout successful</p>
            <Check className="sl-w-5 sl-h-5" />
          </>
        ) : (
          <>
            <p suppressHydrationWarning>{label || "Checkout"}</p>
            <ShoppingBag className="sl-w-5 sl-h-5" />
          </>
        )}
      </Button>

      <div className="sl-flex sl-items-center sl-gap-2 sl-justify-center sl-mt-4 sl-text-muted-foreground">
        <p className="sl-text-sm">Powered by Slice</p>
        <SliceLogo className="sl-w-[14px] sl-h-[14px]" />
      </div>
    </div>
  )
}

export default CheckoutButton
